import { Component, input, output, signal, computed, inject } from '@angular/core';
import { LoggerProvider } from '@shared';
import { IconComponent } from '../icon/icon.component';
import { ButtonComponent } from '../button/button.component';
import { SpinnerComponent } from '../spinner/spinner.component';

@Component({
  selector: 'cm-ai-summary-box',
  standalone: true,
  imports: [IconComponent, ButtonComponent, SpinnerComponent],
  templateUrl: './ai-summary-box.component.html',
  styleUrl: './ai-summary-box.component.less',
})
export class AiSummaryBoxComponent {
  private readonly _logger = inject(LoggerProvider).createScoped('AiSummaryBox');

  // Inputs
  public readonly summary = input.required<string>();
  public readonly recentUpdates = input<string[]>([]);
  public readonly showActions = input<boolean>(true);
  public readonly loading = input<boolean>(false);
  public readonly error = input<string | null>(null);

  // State
  private readonly _TRUNCATE_LENGTH = 200;
  private readonly _isExpanded = signal<boolean>(false);

  // Computed
  public readonly isExpanded = computed(() => this._isExpanded());
  public readonly hasError = computed(() => this.error() !== null);

  public readonly shouldTruncate = computed(() =>
    this.summary().length > this._TRUNCATE_LENGTH
  );

  public readonly parsedSections = computed(() => {
    const text = this.summary();
    const sectionHeaders = ['SUMMARY', 'KEY FACTS', 'RECENT ACTIVITY', 'ACTION ITEMS', 'CONFIDENCE'];
    const pattern = new RegExp(`(${sectionHeaders.join('|')})\\s+`, 'g');
    const sections: { title: string; content: string }[] = [];
    const matches = [...text.matchAll(pattern)];

    if (matches.length === 0) {
      return [{ title: '', content: text }];
    }

    for (let i = 0; i < matches.length; i++) {
      const start = matches[i].index! + matches[i][0].length;
      const end = i + 1 < matches.length ? matches[i + 1].index! : text.length;
      sections.push({ title: matches[i][1], content: text.substring(start, end).trim() });
    }

    return sections;
  });

  public readonly displayedSummary = computed(() => {
    const text = this.summary();
    if (!this.shouldTruncate() || this._isExpanded()) {
      return text;
    }
    return text.substring(0, this._TRUNCATE_LENGTH) + '...';
  });

  public readonly showToggle = computed(() => this.shouldTruncate());

  public readonly toggleLabel = computed(() => (this._isExpanded() ? 'Read less' : 'Read more'));

  // Outputs
  public readonly retry = output<void>();
  public readonly cancelled = output<void>();
  public readonly copied = output<void>();
  public readonly copyFailed = output<void>();

  // Public methods
  public toggleExpanded(): void {
    this._isExpanded.set(!this._isExpanded());
  }

  public handleRetry(): void {
    this.retry.emit();
  }

  public async onCopy(): Promise<void> {
    const content = this._getFormattedContent();
    try {
      await navigator.clipboard.writeText(content);
      this.copied.emit();
    } catch (err) {
      this._logger.error('Failed to copy to clipboard', err);
      this.copyFailed.emit();
    }
  }

  public onCancel(): void {
    // Emit cancel event to parent
    this.cancelled.emit();
  }

  private _getFormattedContent(): string {
    let content = this.summary();

    const updates = this.recentUpdates();
    if (updates.length > 0) {
      content += '\n\nRECENT UPDATES\n';
      updates.forEach((update) => {
        content += `• ${update}\n`;
      });
    }

    return content;
  }
}
